import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { authApi, LoginResponse, RegisterRequest } from '@/lib/api-client';
import { useAuthStore, User } from '../store/authStore';
import { setAuthToken } from '@/lib/api-client';
import {
  clearActiveStudentId,
  listPairedStudents,
} from '@/features/student-auth/deviceAuth';

export function useLogin() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ username, password }: { username: string; password: string }) =>
      authApi.login(username, password),
    onSuccess: (data: LoginResponse) => {
      const profilePayload: User['profile'] = data.user.profile
        ? {
            id: data.user.profile.id,
            type: data.user.profile.profile_type,
            firstName: data.user.profile.first_name ?? null,
            lastName: data.user.profile.last_name ?? null,
            displayName: data.user.profile.display_name ?? null,
            preferredName: data.user.profile.preferred_name ?? null,
            gradeLevel: data.user.profile.grade_level ?? null,
            createdAt: data.user.profile.created_at,
            updatedAt: data.user.profile.updated_at,
          }
        : undefined;

      const user: User = {
        id: data.user.id,
        username: data.user.username,
        email: data.user.email ?? null,
        roles: data.user.roles,
        firstName: data.user.first_name ?? null,
        lastName: data.user.last_name ?? null,
        picture: data.user.picture ?? null,
        grade: profilePayload?.gradeLevel ?? null,
        profile: profilePayload,
        studentId: profilePayload?.id ?? null,
      };

      // Store auth data
      login(user, data.access_token);
      setAuthToken(data.access_token);

      // Navigate to appropriate home based on roles
      // If user has parent role, go to parent view, otherwise student view
      navigate(user.roles.includes('parent') ? '/parent' : '/');
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Login failed',
        message: error.message,
        color: 'red',
      });
    },
  });
}

export function useRegister() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: (data: LoginResponse) => {
      // Backend now returns LoginResponse directly (auto-login on register).
      const profilePayload: User['profile'] = data.user.profile
        ? {
            id: data.user.profile.id,
            type: data.user.profile.profile_type,
            firstName: data.user.profile.first_name ?? null,
            lastName: data.user.profile.last_name ?? null,
            displayName: data.user.profile.display_name ?? null,
            preferredName: data.user.profile.preferred_name ?? null,
            gradeLevel: data.user.profile.grade_level ?? null,
            createdAt: data.user.profile.created_at,
            updatedAt: data.user.profile.updated_at,
          }
        : undefined;

      const user: User = {
        id: data.user.id,
        username: data.user.username,
        email: data.user.email ?? null,
        roles: data.user.roles,
        firstName: data.user.first_name ?? null,
        lastName: data.user.last_name ?? null,
        picture: data.user.picture ?? null,
        grade: profilePayload?.gradeLevel ?? null,
        profile: profilePayload,
        studentId: profilePayload?.id ?? null,
      };

      login(user, data.access_token);
      setAuthToken(data.access_token);
      queryClient.invalidateQueries();

      notifications.show({
        title: 'Welcome!',
        message: "Account created. You're signed in.",
        color: 'green',
      });

      navigate(user.roles.includes('parent') ? '/parent' : '/');
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Registration failed',
        message: error.message,
        color: 'red',
      });
    },
  });
}

export function useGoogleLogin() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (idToken: string) => authApi.googleLogin(idToken),
    onSuccess: (data: LoginResponse) => {
      const profilePayload: User['profile'] = data.user.profile
        ? {
            id: data.user.profile.id,
            type: data.user.profile.profile_type,
            firstName: data.user.profile.first_name ?? null,
            lastName: data.user.profile.last_name ?? null,
            displayName: data.user.profile.display_name ?? null,
            preferredName: data.user.profile.preferred_name ?? null,
            gradeLevel: data.user.profile.grade_level ?? null,
            createdAt: data.user.profile.created_at,
            updatedAt: data.user.profile.updated_at,
          }
        : undefined;

      const user: User = {
        id: data.user.id,
        username: data.user.username,
        email: data.user.email ?? null,
        roles: data.user.roles,
        firstName: data.user.first_name ?? null,
        lastName: data.user.last_name ?? null,
        picture: data.user.picture ?? null,
        grade: profilePayload?.gradeLevel ?? null,
        profile: profilePayload,
        studentId: profilePayload?.id ?? null,
      };

      login(user, data.access_token);
      setAuthToken(data.access_token);

      navigate(user.roles.includes('parent') ? '/parent' : '/');
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Google sign-in failed',
        message: error.message,
        color: 'red',
      });
    },
  });
}

export function useGoogleRegister() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ idToken, inviteToken }: { idToken: string; inviteToken: string }) =>
      authApi.googleRegister(idToken, inviteToken),
    onSuccess: (data: LoginResponse) => {
      const profilePayload: User['profile'] = data.user.profile
        ? {
            id: data.user.profile.id,
            type: data.user.profile.profile_type,
            firstName: data.user.profile.first_name ?? null,
            lastName: data.user.profile.last_name ?? null,
            displayName: data.user.profile.display_name ?? null,
            preferredName: data.user.profile.preferred_name ?? null,
            gradeLevel: data.user.profile.grade_level ?? null,
            createdAt: data.user.profile.created_at,
            updatedAt: data.user.profile.updated_at,
          }
        : undefined;

      const user: User = {
        id: data.user.id,
        username: data.user.username,
        email: data.user.email ?? null,
        roles: data.user.roles,
        firstName: data.user.first_name ?? null,
        lastName: data.user.last_name ?? null,
        picture: data.user.picture ?? null,
        grade: profilePayload?.gradeLevel ?? null,
        profile: profilePayload,
        studentId: profilePayload?.id ?? null,
      };

      login(user, data.access_token);
      setAuthToken(data.access_token);
      queryClient.invalidateQueries();

      notifications.show({
        title: 'Welcome!',
        message: "Account created. You're signed in.",
        color: 'green',
      });

      navigate(user.roles.includes('parent') ? '/parent' : '/');
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Google registration failed',
        message: error.message,
        color: 'red',
      });
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return () => {
    logout();
    setAuthToken(null);

    // Also clear active kid, so a kid device tapping "Logout" lands on the
    // chooser (or /login if only one kid is paired and there's no active).
    // Paired-student metadata and device tokens are preserved so switching
    // back to a paired kid doesn't require re-pairing.
    clearActiveStudentId();

    // Clear all React Query caches to prevent data leakage between users
    queryClient.clear();

    // Clear session-specific flags
    sessionStorage.removeItem('assignments-notification-shown');

    // On a paired device with kids still linked, "Logout" means "switch
    // reader" — send to the chooser. Otherwise fall through to the login
    // screen for parents.
    if (listPairedStudents().length > 0) {
      navigate('/kid');
    } else {
      navigate('/login');
    }
  };
}
